import { Button, Chip, FormControl, Grid, MenuItem } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import GKModal from "components/GKModal";
import GKSelect from "components/GKSelect";
import GKTextField from "components/GKTextField";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import axiosInstance from "utils/axiosInstance";

const CreateBrokerModal = (props) => {
  const { open, setOpen, refetch, brokerData } = props;
  const [tagInput, setTagInput] = useState("");

  const { mutate: handleCreateBroker } = useMutation({
    mutationKey: ["CREATE_BROKER"],
    mutationFn: (data) => axiosInstance.post("/broker/create/", data),
    onSuccess: () => {
      refetch();
      setOpen(false);
    },
    onError: () => {},
  });

  const { mutate: handleUpdateBroker } = useMutation({
    mutationKey: ["UPDATE_BROKER"],
    mutationFn: (data) =>
      axiosInstance.patch(`/broker/${brokerData?.id}/update/`, data),
    onSuccess: () => {
      refetch();
      setOpen(false);
    },
  });

  const form = useForm({
    defaultValues: {
      name: brokerData?.id ? brokerData.name : "",
      status: brokerData?.id ? brokerData.status : "",
      meta: brokerData?.id ? JSON.parse(brokerData?.meta) : [],
      brokerage: brokerData?.id ? brokerData.brokerage : "",
      stt: brokerData?.id ? brokerData.stt : "",
      stampDuty: brokerData?.id ? brokerData.stampDuty : "",
      sgst: brokerData?.id ? brokerData.sgst : "",
      cgst: brokerData?.id ? brokerData.cgst : "",
      igst: brokerData?.id ? brokerData.igst : "",
      stf: brokerData?.id ? brokerData.stf : "",
      etcBse: brokerData?.id ? brokerData.etcBse : "",
      etcNse: brokerData?.id ? brokerData.etcNse : "",
      ot: brokerData?.id ? brokerData.ot : "",
    },
  });

  const handleTagAdd = () => {
    if (tagInput?.trim() !== "") {
      const tags = tagInput.split(",").map((tag) => tag?.trim());
      form.setValue("meta", [...form.getValues().meta, ...tags]);
      setTagInput("");
    }
  };

  const handleTagDelete = (tagIndex) => {
    const updatedTags = [...form.getValues().meta];
    updatedTags.splice(tagIndex, 1);
    form.setValue("meta", updatedTags);
  };

  return (
    <GKModal
      open={open}
      setOpen={setOpen}
      modalTitle={brokerData?.id ? "Update Broker" : "Add New Broker"}
      footer={
        <Grid container justifyContent={"flex-end"} gap={1}>
          <Button
            sx={{
              fontSize: "0.7rem",
              fontFamily: '"Arial", sans-serif',
              textTransform: "none",
              mr: 2,
            }}
            variant="outlined"
            onClick={() => setOpen(false)}
          >
            Close
          </Button>
          <Button
            sx={{
              fontSize: "0.7rem",
              fontFamily: '"Arial", sans-serif',
              textTransform: "none",
              mr: 2,
            }}
            type="submit"
            form="create-broker-form"
            variant="outlined"
          >
            {brokerData?.id ? "Update" : "Submit"}
          </Button>
        </Grid>
      }
    >
      <form
        id="create-broker-form"
        onSubmit={form.handleSubmit((values) => {
          const data = {
            ...values,
            meta: JSON.stringify(values.meta),
            status: values.status === "true",
          };

          if (brokerData?.id) {
            handleUpdateBroker(data);
          } else {
            handleCreateBroker(data);
          }
        })}
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl variant="standard" fullWidth>
              <Controller
                name="name"
                control={form.control}
                render={({ field }) => (
                  <GKTextField {...field} inputLabel="Name" />
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl variant="standard" fullWidth>
              <Controller
                name="status"
                control={form.control}
                render={({ field }) => (
                  <GKSelect {...field} inputLabel="Status">
                    <MenuItem value={"true"}>Active</MenuItem>
                    <MenuItem value={"false"}>Inactive</MenuItem>
                  </GKSelect>
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl variant="standard" fullWidth>
              <Controller
                name="meta"
                control={form.control}
                onChange={(event) => setTagInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    handleTagAdd();
                  }
                }}
                render={({ field }) => (
                  <FormControl variant="standard" fullWidth>
                    <GKTextField
                      {...field}
                      variant="outlined"
                      inputLabel="Meta"
                    />
                  </FormControl>
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl variant="standard" fullWidth>
              <Controller
                name="brokerage"
                control={form.control}
                render={({ field }) => (
                  <GKTextField
                    {...field}
                    type="number"
                    inputLabel="Brokerage"
                  />
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl variant="standard" fullWidth>
              <Controller
                name="stt"
                control={form.control}
                render={({ field }) => (
                  <GKTextField {...field} type="number" inputLabel="STT" />
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl variant="standard" fullWidth>
              <Controller
                name="stampDuty"
                control={form.control}
                render={({ field }) => (
                  <GKTextField
                    {...field}
                    type="number"
                    inputLabel="Stamp Duty"
                  />
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl variant="standard" fullWidth>
              <Controller
                name="sgst"
                control={form.control}
                render={({ field }) => (
                  <GKTextField {...field} type="number" inputLabel="SGST" />
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl variant="standard" fullWidth>
              <Controller
                name="cgst"
                control={form.control}
                render={({ field }) => (
                  <GKTextField {...field} type="number" inputLabel="CGST" />
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl variant="standard" fullWidth>
              <Controller
                name="igst"
                control={form.control}
                render={({ field }) => (
                  <GKTextField {...field} type="number" inputLabel="IGST" />
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl variant="standard" fullWidth>
              <Controller
                name="stf"
                control={form.control}
                render={({ field }) => (
                  <GKTextField
                    {...field}
                    type="number"
                    inputLabel="SEBI Turnover Fees"
                  />
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl variant="standard" fullWidth>
              <Controller
                name="etcBse"
                control={form.control}
                render={({ field }) => (
                  <GKTextField
                    {...field}
                    type="number"
                    inputLabel="Exchange Transaction Charges-BSE"
                  />
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl variant="standard" fullWidth>
              <Controller
                name="etcNse"
                control={form.control}
                render={({ field }) => (
                  <GKTextField
                    {...field}
                    type="number"
                    inputLabel="Exchange Transaction Charges-NSE"
                  />
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl variant="standard" fullWidth>
              <Controller
                name="ot"
                control={form.control}
                render={({ field }) => (
                  <GKTextField
                    {...field}
                    type="number"
                    inputLabel="Other Charges"
                  />
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} display="flex" gap={1}>
            {form.getValues().meta.map((tag, index) => (
              <Chip
                key={index}
                inputLabel={tag}
                onDelete={() => handleTagDelete(index)}
              />
            ))}
          </Grid>
        </Grid>
      </form>
    </GKModal>
  );
};

export default CreateBrokerModal;
